import { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Settings, Loader2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

/** Per-quiz settings dialog: placement (inline ↔ assessment), pass threshold,
 *  sample-from-bank binding. Used from ModuleEditorPage's quiz sections. */
export default function QuizSettingsDialog({ open, onClose, quizId, moduleEditorBackPath }) {
  const { get, put } = useApi();
  const [quiz, setQuiz] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const load = useCallback(async () => {
    if (!quizId) return;
    setLoading(true);
    try {
      const q = await get(`/quizzes/${quizId}`);
      setQuiz(q);
      const pending = await get(`/quizzes/${quizId}/questions/pending`);
      setPendingCount(Array.isArray(pending) ? pending.length : 0);
      const bl = await get('/question-banks?limit=100');
      setBanks(bl.banks || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  }, [get, quizId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const updateField = (field, value) => setQuiz(q => q ? { ...q, [field]: value } : q);

  const handleSave = async () => {
    if (!quiz) return;
    setSaving(true);
    try {
      const body = {
        title: quiz.title,
        pass_threshold: parseFloat(quiz.pass_threshold) || 70,
        max_attempts: parseInt(quiz.max_attempts, 10) || 3,
        time_limit_minutes: quiz.time_limit_minutes ? parseInt(quiz.time_limit_minutes, 10) : null,
        shuffle_questions: !!quiz.shuffle_questions,
        shuffle_options: !!quiz.shuffle_options,
        quiz_type: quiz.quiz_type,
        placement: quiz.placement,
        sample_from_bank: quiz.sample_from_bank || {},
      };
      await put(`/quizzes/${quizId}`, body);
      toast.success('Quiz settings saved');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const isAssessment = quiz?.quiz_type === 'assessment';
  const togglePromote = (next) => {
    if (next) {
      updateField('quiz_type', 'assessment');
      updateField('placement', 'module_terminal');
    } else {
      updateField('quiz_type', 'inline_quiz');
      updateField('placement', 'section_break');
    }
  };

  const sampleCfg = quiz?.sample_from_bank || {};
  const sampleEnabled = !!(sampleCfg && sampleCfg.bank_id);
  const setSample = (patch) => updateField('sample_from_bank', { ...(sampleCfg || {}), ...patch });
  const clearSample = () => updateField('sample_from_bank', {});

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="quiz-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="w-4 h-4" />Quiz Settings</DialogTitle>
        </DialogHeader>

        {loading || !quiz ? (
          <div className="py-12 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4 py-1">
            {/* Title + type */}
            <div>
              <label className="text-xs font-medium">Title</label>
              <Input value={quiz.title || ''} onChange={(e) => updateField('title', e.target.value)} data-testid="quiz-title" />
            </div>

            <label className="flex items-start gap-2 p-3 rounded-lg border bg-muted/30 cursor-pointer">
              <input type="checkbox" checked={isAssessment} onChange={(e) => togglePromote(e.target.checked)}
                className="mt-0.5" data-testid="promote-toggle" />
              <span className="text-sm">
                <span className="font-medium">Promote to module-terminal Assessment</span>
                <span className="block text-[11px] text-muted-foreground mt-0.5">Issues a certificate on pass. Only one terminal Assessment per module is recommended.</span>
              </span>
            </label>

            {/* Pass / attempts / time */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium">Pass %</label>
                <Input type="number" min="0" max="100" value={quiz.pass_threshold ?? 70}
                  onChange={(e) => updateField('pass_threshold', e.target.value)} data-testid="pass-threshold" />
              </div>
              <div>
                <label className="text-xs font-medium">Max attempts</label>
                <Input type="number" min="0" value={quiz.max_attempts ?? 3}
                  onChange={(e) => updateField('max_attempts', e.target.value)} data-testid="max-attempts" />
              </div>
              <div>
                <label className="text-xs font-medium">Time (min)</label>
                <Input type="number" min="1" placeholder="None" value={quiz.time_limit_minutes ?? ''}
                  onChange={(e) => updateField('time_limit_minutes', e.target.value)} data-testid="time-limit" />
              </div>
            </div>

            {/* Shuffles */}
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={!!quiz.shuffle_questions}
                  onChange={(e) => updateField('shuffle_questions', e.target.checked)} data-testid="shuffle-questions" />
                Shuffle questions
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={!!quiz.shuffle_options}
                  onChange={(e) => updateField('shuffle_options', e.target.checked)} data-testid="shuffle-options" />
                Shuffle options
              </label>
            </div>

            {/* Sample from bank */}
            <div className="rounded-lg border p-3 space-y-2" data-testid="sample-from-bank-block">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={sampleEnabled}
                  onChange={(e) => e.target.checked
                    ? setSample({ bank_id: banks[0]?.bank_id || '', sample_size: 5 })
                    : clearSample()
                  } data-testid="sample-toggle" />
                <span className="text-sm font-medium">Pull questions from a Question Bank</span>
              </label>
              {sampleEnabled && (
                <div className="grid grid-cols-2 gap-2 pl-6">
                  <div>
                    <label className="text-[11px] text-muted-foreground">Bank</label>
                    <select className="w-full text-sm p-2 rounded-md border bg-background"
                      value={sampleCfg.bank_id || ''}
                      onChange={(e) => setSample({ bank_id: e.target.value })}
                      data-testid="bank-select">
                      <option value="">— select —</option>
                      {banks.map(b => <option key={b.bank_id} value={b.bank_id}>{b.title} ({b.published_item_count} published)</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-muted-foreground">Sample size</label>
                    <Input type="number" min="1" max="100" value={sampleCfg.sample_size ?? 5}
                      onChange={(e) => setSample({ sample_size: parseInt(e.target.value, 10) || 1 })}
                      data-testid="sample-size" />
                  </div>
                </div>
              )}
            </div>

            {/* Review queue link */}
            {pendingCount > 0 && (
              <div className="rounded-lg border bg-amber-500/10 border-amber-500/30 p-3 text-sm flex items-center justify-between">
                <span><strong>{pendingCount}</strong> AI question{pendingCount === 1 ? '' : 's'} pending review</span>
                <Link to={`/editor/quizzes/${quizId}/review`} state={{ back: moduleEditorBackPath }}
                  className="inline-flex items-center gap-1 text-[var(--lumyn-content)] hover:underline text-xs"
                  data-testid="review-queue-link">
                  Open review queue <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="save-quiz-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
